// ParticipantMedicationChart.jsx (participant app)
import { useState } from 'react';
import dayjs from 'dayjs';
import {
  Box, Paper, Typography, Button, Chip, Modal, Stack, TextField, MenuItem,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useParticipantEPR } from '../ParticipantEPRContext';
import { useSession } from '../SessionContext'; // to get socket + room if you want live updates

const ACCENT = 'rgb(219, 234, 254)';
const ACCENT_BORDER = '#60A5FA';

export default function ParticipantMedicationChart() {
  const { eprData, loading, reload } = useParticipantEPR();
  const { socket, room, liveId } = useSession();
  const meds = eprData?.medications || [];
  const API_BASE = process.env.REACT_APP_API_URL;

  // Add administration modal
  const [open, setOpen] = useState(false);
  const [selectedMed, setSelectedMed] = useState(null);
  const [adminAt, setAdminAt] = useState(dayjs());
  const [saving, setSaving] = useState(false);

  // New prescription modal
  const [rxOpen, setRxOpen] = useState(false);
  const [rxSaving, setRxSaving] = useState(false);
  const [rxName, setRxName] = useState('');
  const [rxDose, setRxDose] = useState('');
  const [rxRoute, setRxRoute] = useState('');
  const [rxFreq, setRxFreq] = useState('');

  // Build columns from what exists
  const allDates = Array.from(new Set(
    meds.flatMap(m => Object.keys(m.administrations ?? {}))
  )).sort((a, b) => new Date(a) - new Date(b));

  const showModal = (med) => {
    setSelectedMed(med);
    setAdminAt(dayjs());
    setOpen(true);
  };
  const closeModal = () => {
    setOpen(false);
    setSelectedMed(null);
  };

  const saveAdministration = async () => {
    if (!selectedMed) return;
    setSaving(true);
    try {
      const iso = adminAt.toISOString();
      await fetch(`${API_BASE}/api/medications/${selectedMed.id}/administrations2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ datetime: iso, room, liveId })
      });
      await reload?.();
      closeModal();
    } catch (e) {
      console.error('add administration failed', e);
      alert('Failed to add administration.');
    } finally {
      setSaving(false);
    }
  };

  const resetRxForm = () => {
    setRxName('');
    setRxDose('');
    setRxRoute('');
    setRxFreq('');
  };

  const closeRxModal = () => {
    if (rxSaving) return;
    setRxOpen(false);
    resetRxForm();
  };

  const savePrescription = async () => {
    if (!rxName.trim() || !rxDose.trim() || !rxRoute.trim() || !rxFreq.trim()) {
      alert('Please fill out name, dose, route, and frequency.');
      return;
    }
    setRxSaving(true);
    try {
      await fetch(`${API_BASE}/api/prescribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          liveId, room,
          name: rxName.trim(),
          dose: rxDose.trim(),
          route: rxRoute.trim(),
          frequency: rxFreq.trim()
        })
      });
      await reload?.();
      closeRxModal();
    } catch (e) {
      console.error('prescribe failed', e);
      alert('Failed to prescribe medication.');
    } finally {
      setRxSaving(false);
    }
  };

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <Paper elevation={0} sx={{ border: `1px solid ${ACCENT_BORDER}`, borderRadius: 3, overflow: 'hidden', bgcolor: 'white' }}>
        {/* Header */}
        <Box sx={{
          px: 2, py: 1.25, bgcolor: ACCENT, borderBottom: `1px solid ${ACCENT_BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Typography sx={{ fontWeight: 800 }}>Medication Chart</Typography>
          <Button
            variant="contained"
            size="small"
            sx={{ textTransform: 'none', boxShadow: 'none' }}
            onClick={() => setRxOpen(true)}
          >
            Prescribe new
          </Button>
        </Box>

        {/* Table */}
        <TableContainer sx={{ maxHeight: 560, overflow: 'auto', borderRadius:0 }}>
          <Table stickyHeader size="small" sx={{  minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    position: 'sticky', left: 0, zIndex: 3,
                    bgcolor: '#e5e7eb', fontWeight: 700, width: 260,
                    borderRight: '1px solid #e5e7eb'
                  }}
                >
                  Medication
                </TableCell>
                {allDates.map((d) => (
                  <TableCell key={d} sx={{ bgcolor: '#e5e7eb', fontWeight: 700, textAlign: 'center' }}>
                    {dayjs(d).isValid() ? dayjs(d).format('DD/MM/YYYY') : d}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={1 + allDates.length} sx={{ textAlign: 'center', py: 3 }}>Administering medication...</TableCell></TableRow>
              )}
              {!loading && meds.length === 0 && (
                <TableRow><TableCell colSpan={1 + allDates.length} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>No medications yet.</TableCell></TableRow>
              )}
              {meds.map((med) => (
                <TableRow key={med.id || med.name} hover>
                  {/* Left sticky */}
                  <TableCell
                    sx={{
                      position: 'sticky', left: 0, zIndex: 2,
                      bgcolor: '#F8FAFF', borderRight: '1px solid #e5e7eb'
                    }}
                  >
                    <div style={{display:'flex', flexDirection:'row', justifyContent:'space-between', alignItems:'center'}}>
                      <div>
                        <Typography sx={{ fontWeight: 700 }}>{med.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Route: {med.route || '—'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Dose: {med.dose || '—'} {med.frequency ? `(${med.frequency})` : ''}
                        </Typography>
                      </div>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => showModal(med)}
                        sx={{ textTransform: 'none', height:'30px' }}
                      >
                        Administer
                      </Button>
                    </div>
                  </TableCell>

                  {/* Columns */}
                  {allDates.map((d) => {
                    const times = (med.administrations?.[d] || []);
                    return (
                      <TableCell key={d} sx={{ textAlign: 'center' }}>
                        {times.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        ) : (
                          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center" justifyContent="center">
                            {times.map((t, i) => <Chip key={i} size="small" label={t} />)}
                          </Stack>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add administration modal */}
      <Modal open={open} onClose={saving ? undefined : closeModal}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 520, maxWidth: '95vw', bgcolor: 'white', borderRadius: 3,
          border: `1px solid ${ACCENT_BORDER}`, boxShadow: 24, overflow: 'hidden'
        }}>
          <Box sx={{ px: 2, py: 1.25, bgcolor: ACCENT, borderBottom: `1px solid ${ACCENT_BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 800 }}>Add administration</Typography>
            {selectedMed && <Chip size="small" label={selectedMed.name} />}
          </Box>

          <Box sx={{ p: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Date & time"
                format="DD/MM/YYYY HH:mm"
                value={adminAt}
                onChange={setAdminAt}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </LocalizationProvider>

            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button onClick={closeModal} disabled={saving}>Cancel</Button>
              <Button variant="contained" onClick={saveAdministration} disabled={saving}>
                {saving ? 'Saving…' : 'Save dose'}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>

      {/* New prescription modal */}
      <Modal open={rxOpen} onClose={rxSaving ? undefined : closeRxModal}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 560, maxWidth: '95vw', bgcolor: 'white', borderRadius: 3,
          border: `1px solid ${ACCENT_BORDER}`, boxShadow: 24, overflow: 'hidden'
        }}>
          <Box sx={{
            px: 2, py: 1.25, bgcolor: ACCENT, borderBottom: `1px solid ${ACCENT_BORDER}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <Typography sx={{ fontWeight: 800 }}>Prescribe medication</Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              <TextField
                label="Medication name"
                value={rxName}
                onChange={(e) => setRxName(e.target.value)}
                size="small"
                fullWidth
              />
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Dose"
                  placeholder="e.g. 1g"
                  value={rxDose}
                  onChange={(e) => setRxDose(e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Route"
                  placeholder="e.g. PO / IV / IM / PR / Neb"
                  value={rxRoute}
                  onChange={(e) => setRxRoute(e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Frequency"
                  placeholder="e.g. OD / BD / TDS / QDS / PRN"
                  value={rxFreq}
                  onChange={(e) => setRxFreq(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button onClick={closeRxModal} disabled={rxSaving}>Cancel</Button>
              <Button variant="contained" onClick={savePrescription} disabled={rxSaving}>
                {rxSaving ? 'Saving…' : 'Prescribe'}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
